import { useCallback, useEffect, useRef } from 'react'
import {
  keyIntentDirection,
  touchIntentDirection,
  wheelIntentDirection,
  type ScrollIntentDirection,
} from '../scroll-intent'

/**
 * How long after a real input event its scroll events keep arriving. Wheel
 * momentum and touch inertia both emit `scroll` well after the last input event,
 * and those frames are still the user's scroll.
 */
const SCROLL_INTENT_WINDOW_MS = 400

/**
 * Scroll keys reach whatever has focus, and the scroller is a plain
 * non-focusable div — Chromium routes key scrolling to it even while focus
 * sits on `body`, so a keydown listener on the element itself would miss
 * exactly the presses that scroll it. Key intent is therefore read at the
 * window, skipping presses aimed at an editable control (typing in the chat
 * input never scrolls the list).
 */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  )
}

/**
 * Watch for the input events that mean "the user is scrolling `element`" —
 * wheel and touch drag on the element, scroll keys at the window (see
 * `isEditableTarget` for why keys are not read from the element).
 *
 * `onIntent` fires synchronously per event, before the resulting `scroll`.
 * Imperative core shared by `useScrollIntent` and effect-scoped callers
 * (the open-on-latest pin's takeover detection); returns a cleanup that
 * removes every listener.
 */
export function observeScrollIntent(
  element: HTMLElement,
  onIntent: (direction: ScrollIntentDirection) => void,
): () => void {
  const registerIntent = (direction: ScrollIntentDirection | null) => {
    if (!direction) return
    onIntent(direction)
  }

  const handleWheel = (event: WheelEvent) => registerIntent(wheelIntentDirection(event.deltaY))
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.defaultPrevented || isEditableTarget(event.target)) return
    // Only count presses that can plausibly scroll THIS list: focus on
    // body/nothing (Chromium routes key scrolling to the nearest scrollable,
    // i.e. the list) or inside the list itself. A key press while focus sits
    // on a sibling pane, button, or popover scrolls that element — reading
    // it as intent here would disengage follow on an unrelated ArrowUp.
    const active = document.activeElement
    if (active && active !== document.body && !element.contains(active)) return
    registerIntent(keyIntentDirection(event.key))
  }

  let touchStartY: number | null = null
  const handleTouchStart = (event: TouchEvent) => {
    touchStartY = event.touches[0]?.clientY ?? null
  }
  const handleTouchMove = (event: TouchEvent) => {
    const currentY = event.touches[0]?.clientY
    if (touchStartY === null || currentY === undefined) return
    const direction = touchIntentDirection(touchStartY, currentY)
    // Hold the anchor through sub-threshold moves so a slow drag accumulates
    // into one rather than being filtered away a pixel at a time.
    if (!direction) return
    touchStartY = currentY
    registerIntent(direction)
  }
  const handleTouchEnd = () => {
    touchStartY = null
  }

  element.addEventListener('wheel', handleWheel, { passive: true })
  window.addEventListener('keydown', handleKeyDown)
  element.addEventListener('touchstart', handleTouchStart, { passive: true })
  element.addEventListener('touchmove', handleTouchMove, { passive: true })
  element.addEventListener('touchend', handleTouchEnd, { passive: true })
  element.addEventListener('touchcancel', handleTouchEnd, { passive: true })

  return () => {
    element.removeEventListener('wheel', handleWheel)
    window.removeEventListener('keydown', handleKeyDown)
    element.removeEventListener('touchstart', handleTouchStart)
    element.removeEventListener('touchmove', handleTouchMove)
    element.removeEventListener('touchend', handleTouchEnd)
    element.removeEventListener('touchcancel', handleTouchEnd)
  }
}

/**
 * Hook wrapper over `observeScrollIntent`: fires `onIntent` per input event,
 * before the resulting `scroll`, so a caller can disengage follow on the first
 * upward flick rather than waiting for a measurement to prove the list moved.
 *
 * Returns a reader for the direction of the user scroll currently in flight, or
 * null when nothing user-driven is.
 */
export function useScrollIntent(
  element: HTMLElement | null,
  onIntent: (direction: ScrollIntentDirection) => void,
): () => ScrollIntentDirection | null {
  const onIntentRef = useRef(onIntent)
  useEffect(() => {
    onIntentRef.current = onIntent
  }, [onIntent])

  // Timestamp and direction are only ever read together, and there is no
  // honest direction before the first input event — one nullable record rather
  // than two refs with a placeholder in one of them.
  const lastIntentRef = useRef<{ at: number; direction: ScrollIntentDirection } | null>(null)

  useEffect(() => {
    if (!element) return
    return observeScrollIntent(element, (direction) => {
      lastIntentRef.current = { at: performance.now(), direction }
      onIntentRef.current(direction)
    })
  }, [element])

  return useCallback(() => {
    const lastIntent = lastIntentRef.current
    if (!lastIntent || performance.now() - lastIntent.at > SCROLL_INTENT_WINDOW_MS) return null
    return lastIntent.direction
  }, [])
}
